import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Description,
  GetApp,
  Visibility,
  LocalShipping,
  Label,
  Assignment,
  CheckCircle,
  Close
} from '@mui/icons-material';
import { useActivityLog } from '../../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import FavoriteButton from '../../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../../constants/colors';

const PalletLabels = () => {
  const theme = useTheme();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [masterFile, setMasterFile] = useState(null);
  const [masterFilePath, setMasterFilePath] = useState('');
  const [retailer, setRetailer] = useState('walmart');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const validateAndSetFile = useCallback((file) => {
    if (file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        '.csv',
        '.xlsx',
        '.xls'
      ];

      const isValidType = allowedTypes.some(type =>
        file.type === type || file.name.toLowerCase().endsWith(type)
      );

      if (isValidType) {
        setMasterFile(file);
        setMasterFilePath(file.name);
        setError('');
        return true;
      } else {
        setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
        return false;
      }
    }
    return false;
  }, []);

  const handleMasterFileUpload = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  };

  // Fetch status for processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.PALLET_LABELS_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        const progressPct = statusData.progressPercentage ?? 0;
        const totalItems = statusData.totalItems ?? 0;
        const processedItems = statusData.processedItems ?? 0;
        const logs = statusData.logs ?? [];
        const currentStatus = statusData.status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Check if processing is complete
        if (currentStatus === 'completed' || currentStatus === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            const loadCountText = statusData.loadCount > 1 ? ` for ${statusData.loadCount} loads` : '';
            setSuccess(`Processing completed! Generated ${statusData.palletCount} pallet labels and ${statusData.bolCount} bills of lading${loadCountText}. Downloading...`);
            setResults({
              loadId: statusData.loadId,
              loadIds: statusData.loadIds || [],
              loadCount: statusData.loadCount || 1,
              palletCount: statusData.palletCount,
              bolCount: statusData.bolCount,
              hasZipFile: statusData.hasZipFile || false,
              processId: currentProcessId
            });

            // Auto-download all files when complete
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              try {
                const response = await apiCall(API_ENDPOINTS.PALLET_LABELS_DOWNLOAD_ALL(currentProcessId));
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `WAL4000_Output_${new Date().toISOString().slice(0, 10)}.zip`;
                link.click();
                window.URL.revokeObjectURL(url);
                setSuccess(`Processing completed! Generated ${statusData.palletCount} pallet labels and ${statusData.bolCount} bills of lading${loadCountText}. Files downloaded.`);
              } catch (downloadErr) {
                console.error('Auto-download failed:', downloadErr);
                setError(`Processing completed but download failed: ${downloadErr.message}`);
              }
            }
          } else {
            setError('Processing failed. Check the activity log for details.');
          }

          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const handleProcessFiles = async () => {
    if (!masterFile) {
      setError('Please select a master file before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);
    downloadTriggeredRef.current = false;

    const retailerDisplay = retailer === 'walmart' ? 'Walmart' : "Sam's Club";

    try {
      const formData = new FormData();
      formData.append('masterFile', masterFile);
      formData.append('retailer', retailer);

      const result = await apiCall(API_ENDPOINTS.PALLET_LABELS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (result.processId) {
        setProcessId(result.processId);
        processIdRef.current = result.processId;

        // Add to global activity log
        addActivityProcess(result.processId, `WAL4000 PDF (${retailerDisplay}): ${masterFile.name}`, 'WAL4000 PDF');

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
      setProcessing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleDownloadSample = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.PALLET_LABELS_SAMPLE);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `WS_Master_Import_Sample_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const resetForm = () => {
    setMasterFile(null);
    setMasterFilePath('');
    setError('');
    setSuccess('');
    setResults(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="WAL4000 PDFs" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          WAL4000 PDF'S
        </Typography>

      <Grid container spacing={3}>
        {/* Main Processing Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              File Processing
            </Typography>

            {/* Retailer Selection */}
            <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShipping sx={{ color: TOOL_COLORS.PDF.primary }} />
                      Select Retailer
                    </Box>
                  </FormLabel>
                  <RadioGroup
                    value={retailer}
                    onChange={(e) => setRetailer(e.target.value)}
                    row
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel value="walmart" control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />} label="Walmart" />
                    <FormControlLabel value="samsclub" control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />} label="Sam's Club" />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>

            {/* Master File Upload */}
            <Card sx={{ mb: 3, bgcolor: masterFile ? getCardBackground(theme) : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Master Import File (Excel/CSV)
                  </Typography>
                  {masterFile && (
                    <Tooltip title="File selected">
                      <Visibility sx={{ color: '#4CAF50' }} />
                    </Tooltip>
                  )}
                </Box>

                {/* Drag and Drop Zone */}
                <Box
                  component="label"
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    display: 'block',
                    border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : masterFile ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: isDragging
                      ? TOOL_COLORS.PDF.light
                      : masterFile
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                        : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: masterFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                      bgcolor: masterFile
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                        : TOOL_COLORS.PDF.light,
                    },
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls,.csv"
                    onChange={handleMasterFileUpload}
                  />
                  {masterFile ? (
                    <Box>
                      <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        1 file selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to replace
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {isDragging ? 'Drop master file here' : 'Drag & drop master file here or click to select'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excel (.xlsx, .xls) or CSV files
                      </Typography>
                    </>
                  )}
                </Box>

                {masterFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      mb: 2,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {masterFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        ({(masterFile.size / 1024).toFixed(1)} KB)
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => { setMasterFile(null); setMasterFilePath(''); }}
                      disabled={processing}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  onClick={handleDownloadSample}
                  startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                  sx={{
                    borderColor: alpha(theme.palette.text.primary, 0.2),
                    color: alpha(theme.palette.text.primary, 0.7),
                    bgcolor: 'transparent',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.8,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(theme.palette.text.primary, 0.4),
                      color: theme.palette.text.primary,
                      bgcolor: alpha(theme.palette.text.primary, 0.05),
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  Download Template
                </Button>
              </CardContent>
            </Card>

            {/* Process Button */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleProcessFiles}
                disabled={!masterFile || processing}
                sx={{ 
                  bgcolor: TOOL_COLORS.PDF.primary,
                  '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                  px: 4
                }}
              >
                {processing ? 'Processing...' : 'Generate Labels & BOL'}
              </Button>
              
              {(masterFile || results) && (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.text.secondary,
                      bgcolor: alpha(theme.palette.text.primary, 0.05)
                    }
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Status & Download Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Status & Downloads
            </Typography>

            {/* Progress */}
            {processing && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing master file...
                </Typography>
                <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Success Alert */}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            {/* Results Summary */}
            {results && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Processing Results:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${results.palletCount} Pallet Labels`}
                      secondary="Individual pallet labels generated"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assignment sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${results.bolCount} Bills of Lading`}
                      secondary="BOL documents created"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocalShipping sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Load ID: ${results.loadId}`}
                      secondary="Shipment identifier"
                    />
                  </ListItem>
                </List>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Instructions */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Instructions:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
              1. Select retailer (Walmart or Sam's Club)<br />
              2. Upload master import file with shipping data<br />
              3. Click "Generate Labels & BOL" to process<br />
              4. Files will automatically download as ZIP when ready<br /><br />
              <strong>File Format:</strong> Excel (.xlsx, .xls) or CSV files with columns: Load, CarrierCode, SCACCode, PONo, ItemDescription, DCNo, Address, etc.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default PalletLabels;